'use client';
import React, { useState } from 'react';
import { Upload, GitBranch, CheckCircle2, Clock, AlertCircle, Eye, Plus, ArrowUpRight, FileText, Zap, Users, Filter } from 'lucide-react';

import Modal from '@/components/ui/Modal';
import Icon from '@/components/ui/AppIcon';


type ReviewStatus = 'draft' | 'pending_review' | 'approved' | 'live' | 'rejected';

interface CurriculumVersion {
  id: string;
  courseId: string;
  courseName: string;
  wing: string;
  version: string;
  prevVersion: string;
  author: string;
  submittedAt: string;
  status: ReviewStatus;
  changeLog: string[];
  reviewer?: string;
  reviewNote?: string;
  liveStudents?: number;
}

const versions: CurriculumVersion[] = [
  {
    id: 'cv-001', courseId: 'cat-016', courseName: 'AI Development', wing: 'professional',
    version: 'v3.2.0', prevVersion: 'v3.1.0', author: 'Dr. Nadia Volkov',
    submittedAt: '2026-07-08', status: 'pending_review',
    changeLog: ['Added LLM fine-tuning module', 'Updated RAG architecture lesson', 'New quiz: Transformer internals'],
    liveStudents: 0,
  },
  {
    id: 'cv-002', courseId: 'cat-010', courseName: 'Advanced Mathematics', wing: 'advanced',
    version: 'v2.1.0', prevVersion: 'v2.0.3', author: 'Prof. Marcus Chen',
    submittedAt: '2026-07-06', status: 'approved',
    changeLog: ['Expanded calculus section', 'Added 3 new proof exercises', 'Fixed typos in module 4'],
    reviewer: 'Admin Steward', reviewNote: 'Excellent additions. Approved for live deployment.',
    liveStudents: 0,
  },
  {
    id: 'cv-003', courseId: 'cat-007', courseName: 'Intro to Coding', wing: 'intermediate',
    version: 'v4.0.0', prevVersion: 'v3.5.2', author: 'Dr. Aisha Patel',
    submittedAt: '2026-07-03', status: 'live',
    changeLog: ['Major rewrite: Python 3.12 syntax', 'New project: Build a calculator', 'AI tutor sync notes updated'],
    reviewer: 'Admin Steward', liveStudents: 1247,
  },
  {
    id: 'cv-004', courseId: 'cat-015', courseName: 'Aerospace Systems', wing: 'professional',
    version: 'v1.3.0', prevVersion: 'v1.2.1', author: 'Col. James Rivera',
    submittedAt: '2026-07-01', status: 'rejected',
    changeLog: ['Added classified mission profiles', 'New propulsion chapter'],
    reviewer: 'Admin Steward', reviewNote: 'Classified content cannot be published. Please revise.',
    liveStudents: 0,
  },
  {
    id: 'cv-005', courseId: 'cat-012', courseName: 'AI Ethics & Safety', wing: 'advanced',
    version: 'v2.0.0', prevVersion: 'v1.9.0', author: 'Dr. Nadia Volkov',
    submittedAt: '2026-07-09', status: 'draft',
    changeLog: ['New module: Algorithmic bias', 'Case study: Social media AI', 'Updated reading list 2026'],
    liveStudents: 0,
  },
];

const statusConfig: Record<ReviewStatus, { label: string; color: string; icon: React.ElementType }> = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: FileText },
  pending_review: { label: 'Pending Review', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
  approved: { label: 'Approved', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  live: { label: 'Live', color: 'bg-green-50 text-green-700 border-green-200', icon: Zap },
  rejected: { label: 'Rejected', color: 'bg-red-50 text-red-700 border-red-200', icon: AlertCircle },
};

const wingColor: Record<string, string> = {
  foundations: 'text-emerald-600 bg-emerald-50',
  intermediate: 'text-blue-600 bg-blue-50',
  advanced: 'text-purple-600 bg-purple-50',
  professional: 'text-amber-600 bg-amber-50',
};

export default function CurriculumManagementContent() {
  const [activeTab, setActiveTab] = useState<'versions' | 'upload' | 'review'>('versions');
  const [selectedVersion, setSelectedVersion] = useState<CurriculumVersion | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [uploadStep, setUploadStep] = useState(1);
  const [filterStatus, setFilterStatus] = useState<ReviewStatus | 'all'>('all');
  const [deployingId, setDeployingId] = useState<string | null>(null);

  const filtered = filterStatus === 'all' ? versions : versions.filter(v => v.status === filterStatus);

  const handleDeploy = async (id: string) => {
    setDeployingId(id);
    await new Promise(r => setTimeout(r, 1500));
    setDeployingId(null);
  };

  const stats = [
    { label: 'Total Versions', value: versions.length, icon: GitBranch, color: 'text-blue-600 bg-blue-50' },
    { label: 'Pending Review', value: versions.filter(v => v.status === 'pending_review').length, icon: Clock, color: 'text-amber-600 bg-amber-50' },
    { label: 'Live Courses', value: versions.filter(v => v.status === 'live').length, icon: Zap, color: 'text-green-600 bg-green-50' },
    { label: 'Students Affected', value: versions.reduce((a, v) => a + (v.liveStudents || 0), 0).toLocaleString(), icon: Users, color: 'text-purple-600 bg-purple-50' },
  ];

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-foreground">Dynamic Curriculum Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Upload, version, review, and deploy curriculum updates in real time</p>
        </div>
        <button
          onClick={() => setActiveTab('upload')}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold btn-scale hover:bg-primary/90 transition-colors"
        >
          <Plus size={16} /> New Curriculum Upload
        </button>
      </div>

      {/* Stats bento */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-card border border-border rounded-2xl p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${s.color}`}>
                <Icon size={18} />
              </div>
              <div>
                <p className="text-xl font-extrabold text-foreground font-tabular">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        {(['versions', 'upload', 'review'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-semibold capitalize transition-all border-b-2 -mb-px ${
              activeTab === tab
                ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'versions' ? 'Version History' : tab === 'upload' ? 'Upload New' : 'Review Queue'}
          </button>
        ))}
      </div>

      {/* Version History Tab */}
      {activeTab === 'versions' && (
        <div className="space-y-4">
          {/* Filter row */}
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={14} className="text-muted-foreground" />
            {(['all', 'draft', 'pending_review', 'approved', 'live', 'rejected'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  filterStatus === s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {s === 'all' ? 'All' : statusConfig[s].label}
              </button>
            ))}
          </div>

          {/* Version list */}
          <div className="space-y-3">
            {filtered.map((v) => {
              const StatusIcon = statusConfig[v.status].icon;
              return (
                <div key={v.id} className="bg-card border border-border rounded-2xl p-5 card-hover">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${wingColor[v.wing]}`}>
                          {v.wing.charAt(0).toUpperCase() + v.wing.slice(1)}
                        </span>
                        <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${statusConfig[v.status].color}`}>
                          <StatusIcon size={10} /> {statusConfig[v.status].label}
                        </span>
                        {v.status === 'live' && v.liveStudents && v.liveStudents > 0 && (
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                            {v.liveStudents.toLocaleString()} students enrolled
                          </span>
                        )}
                      </div>
                      <h3 className="text-base font-bold text-foreground">{v.courseName}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <GitBranch size={11} /> {v.prevVersion} → <span className="font-semibold text-primary">{v.version}</span>
                        </span>
                        <span className="text-xs text-muted-foreground">· by {v.author} · {v.submittedAt}</span>
                      </div>
                      <ul className="mt-2 space-y-0.5">
                        {v.changeLog.map((c, i) => (
                          <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                            <span className="text-primary mt-0.5">•</span> {c}
                          </li>
                        ))}
                      </ul>
                      {v.reviewNote && (
                        <div className={`mt-2 text-xs px-3 py-2 rounded-lg ${v.status === 'rejected' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                          <span className="font-semibold">{v.reviewer}:</span> {v.reviewNote}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => { setSelectedVersion(v); setDetailOpen(true); }}
                        className="flex items-center gap-1.5 px-3 py-2 bg-muted hover:bg-muted/80 rounded-lg text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Eye size={13} /> View
                      </button>
                      {v.status === 'approved' && (
                        <button
                          onClick={() => handleDeploy(v.id)}
                          disabled={deployingId === v.id}
                          className="flex items-center gap-1.5 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-xs font-semibold btn-scale hover:bg-primary/90 transition-colors"
                        >
                          {deployingId === v.id ? (
                            <span className="w-3 h-3 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                          ) : (
                            <><Zap size={13} /> Deploy Live</>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upload Tab */}
      {activeTab === 'upload' && (
        <div className="max-w-2xl space-y-6">
          {/* Step indicator */}
          <div className="flex items-center gap-0">
            {[1, 2, 3].map((step, i) => (
              <React.Fragment key={step}>
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${uploadStep >= step ? 'text-primary' : 'text-muted-foreground'}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${uploadStep > step ? 'bg-primary text-primary-foreground' : uploadStep === step ? 'bg-primary/10 text-primary border-2 border-primary' : 'bg-muted text-muted-foreground'}`}>
                    {uploadStep > step ? <CheckCircle2 size={12} /> : step}
                  </span>
                  {step === 1 ? 'Course Info' : step === 2 ? 'Upload Content' : 'Review & Submit'}
                </div>
                {i < 2 && <div className={`flex-1 h-0.5 mx-1 ${uploadStep > step ? 'bg-primary' : 'bg-muted'}`} />}
              </React.Fragment>
            ))}
          </div>

          {uploadStep === 1 && (
            <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
              <h2 className="text-base font-bold text-foreground">Course Information</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">Course Title</label>
                  <input className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30" placeholder="e.g. Advanced Quantum Computing" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">Campus Wing</label>
                  <select className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30">
                    <option>Foundations (6–9)</option>
                    <option>Intermediate (10–13)</option>
                    <option>Advanced (14–17)</option>
                    <option>Professional (18+)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">Version Number</label>
                  <input className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30" placeholder="e.g. v1.0.0" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">Change Log Summary</label>
                  <textarea rows={3} className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30 resize-none" placeholder="Describe what's new or changed in this version..." />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">AI Tutor Sync Notes</label>
                  <textarea rows={2} className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30 resize-none" placeholder="Instructions for the AI tutor to adapt to this new content..." />
                </div>
              </div>
              <button onClick={() => setUploadStep(2)} className="w-full py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold btn-scale hover:bg-primary/90 transition-colors">
                Continue to Upload Content →
              </button>
            </div>
          )}

          {uploadStep === 2 && (
            <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
              <h2 className="text-base font-bold text-foreground">Upload Curriculum Content</h2>
              <div className="border-2 border-dashed border-border rounded-2xl p-8 text-center hover:border-primary/40 transition-colors cursor-pointer group">
                <Upload size={32} className="mx-auto text-muted-foreground group-hover:text-primary transition-colors mb-3" />
                <p className="text-sm font-semibold text-foreground">Drop curriculum files here</p>
                <p className="text-xs text-muted-foreground mt-1">Supports: PDF, DOCX, MP4, ZIP, SCORM packages</p>
                <button className="mt-3 px-4 py-2 bg-primary/10 text-primary rounded-lg text-xs font-semibold hover:bg-primary/20 transition-colors">
                  Browse Files
                </button>
              </div>
              <div className="space-y-2">
                {['Module 1 - Introduction.pdf', 'Module 2 - Core Concepts.pdf', 'Quiz Bank v3.2.docx', 'Video Lectures.zip'].map((f) => (
                  <div key={f} className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
                    <FileText size={14} className="text-primary shrink-0" />
                    <span className="text-sm text-foreground flex-1">{f}</span>
                    <CheckCircle2 size={14} className="text-emerald-500" />
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <button onClick={() => setUploadStep(1)} className="flex-1 py-2.5 border border-border rounded-xl text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors">← Back</button>
                <button onClick={() => setUploadStep(3)} className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold btn-scale hover:bg-primary/90 transition-colors">Continue to Review →</button>
              </div>
            </div>
          )}

          {uploadStep === 3 && (
            <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
              <h2 className="text-base font-bold text-foreground">Review & Submit for Approval</h2>
              <div className="space-y-3">
                {[
                  { label: 'Course', value: 'Advanced Quantum Computing' },
                  { label: 'Wing', value: 'Professional (18+)' },
                  { label: 'Version', value: 'v1.0.0' },
                  { label: 'Files', value: '4 files uploaded' },
                  { label: 'Review Route', value: 'Peer Review → Admin Approval → Live' },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <span className="text-sm text-muted-foreground">{row.label}</span>
                    <span className="text-sm font-semibold text-foreground">{row.value}</span>
                  </div>
                ))}
              </div>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
                <span className="font-semibold">Submission Note:</span> This curriculum will enter the peer review queue. You'll receive a notification when a reviewer is assigned.
              </div>
              <div className="flex gap-3">
                <button onClick={() => setUploadStep(2)} className="flex-1 py-2.5 border border-border rounded-xl text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors">← Back</button>
                <button onClick={() => setUploadStep(1)} className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold btn-scale hover:bg-primary/90 transition-colors">
                  <CheckCircle2 size={14} className="inline mr-1.5" />Submit for Review
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Review Queue Tab */}
      {activeTab === 'review' && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h2 className="text-base font-bold text-foreground">Pending Review Queue</h2>
              <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-semibold">
                {versions.filter(v => v.status === 'pending_review').length} awaiting review
              </span>
            </div>
            <div className="divide-y divide-border">
              {versions.filter(v => v.status === 'pending_review' || v.status === 'approved').map((v) => (
                <div key={v.id} className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${wingColor[v.wing]}`}>{v.wing}</span>
                      <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${statusConfig[v.status].color}`}>
                        {statusConfig[v.status].label}
                      </span>
                    </div>
                    <p className="text-sm font-bold text-foreground">{v.courseName} — {v.version}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Submitted by {v.author} on {v.submittedAt}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-100 transition-colors">
                      Reject
                    </button>
                    <button className="px-3 py-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg text-xs font-semibold hover:bg-emerald-100 transition-colors">
                      Approve
                    </button>
                    <button className="px-3 py-2 bg-primary text-primary-foreground rounded-lg text-xs font-semibold btn-scale hover:bg-primary/90 transition-colors">
                      Full Review
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Review pipeline diagram */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <h3 className="text-sm font-bold text-foreground mb-4">Review & Publish Pipeline</h3>
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {[
                { label: 'Draft', icon: FileText, color: 'bg-slate-100 text-slate-600' },
                { label: 'Peer Review', icon: Users, color: 'bg-blue-50 text-blue-600' },
                { label: 'Admin Approval', icon: CheckCircle2, color: 'bg-amber-50 text-amber-600' },
                { label: 'Live Deploy', icon: Zap, color: 'bg-green-50 text-green-600' },
              ].map((stage, i) => {
                const Icon = stage.icon;
                return (
                  <React.Fragment key={stage.label}>
                    <div className={`flex flex-col items-center gap-1.5 px-4 py-3 rounded-xl shrink-0 ${stage.color}`}>
                      <Icon size={18} />
                      <span className="text-xs font-semibold whitespace-nowrap">{stage.label}</span>
                    </div>
                    {i < 3 && <ArrowUpRight size={16} className="text-muted-foreground shrink-0 rotate-45" />}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <Modal open={detailOpen} onClose={() => setDetailOpen(false)} title="Curriculum Version Detail" size="md">
        {selectedVersion && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${wingColor[selectedVersion.wing]}`}>{selectedVersion.wing}</span>
              <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${statusConfig[selectedVersion.status].color}`}>
                {statusConfig[selectedVersion.status].label}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground">{selectedVersion.courseName}</h3>
              <p className="text-sm text-muted-foreground">{selectedVersion.prevVersion} → {selectedVersion.version} · by {selectedVersion.author}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Change Log</p>
              <ul className="space-y-1.5">
                {selectedVersion.changeLog.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                    <CheckCircle2 size={14} className="text-primary shrink-0 mt-0.5" /> {c}
                  </li>
                ))}
              </ul>
            </div>
            {selectedVersion.reviewNote && (
              <div className={`p-3 rounded-xl text-sm ${selectedVersion.status === 'rejected' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                <span className="font-semibold">{selectedVersion.reviewer}:</span> {selectedVersion.reviewNote}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
